import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, X, CreditCard, ShieldCheck, Fingerprint, AlertTriangle, Wallet, ImageOff, Loader2, Maximize2 } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, Pager, Toolbar, Select, RefreshButton, Status, Tag, ConfirmButton, Drawer } from '../ui'
import { get, post, qs } from '../../lib/api'
import { toast } from '../../store'
import { clsx, fa, faDate, num, asArray } from '../../lib/format'

const BASE = import.meta.env.VITE_API_URL || ''

/**
 * نمایش مدرکی که نشانی‌اش عمومی نیست.
 *
 * تگ `<img>` هدر Authorization نمی‌فرستد، و مدرک هویتی هم نباید نشانی باز
 * داشته باشد. پس فایل با `fetch` و توکن گرفته می‌شود و به‌صورت Object URL
 * نمایش داده می‌شود — نشانی‌ای که فقط در همین تب زنده است.
 */
function AuthImage({ url, alt, className }: { url: string; alt: string; className?: string }) {
  const [src, setSrc] = useState('')
  const [err, setErr] = useState('')

  useEffect(() => {
    let dead = false
    let made = ''
    const token = localStorage.getItem('az_token')
    fetch(`${BASE}${url}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? 'فایل مدرک روی سرور نیست' : 'خطا در دریافت مدرک')
        const blob = await r.blob()
        if (dead) return
        made = URL.createObjectURL(blob)
        setSrc(made)
      })
      .catch((e) => { if (!dead) setErr(e.message) })
    return () => { dead = true; if (made) URL.revokeObjectURL(made) }
  }, [url])

  if (err) {
    return (
      <div className={clsx('grid place-items-center gap-1 bg-white/[.03] text-[10px] text-slate-500', className)}>
        <ImageOff className="h-4 w-4" />{err}
      </div>
    )
  }
  if (!src) {
    return (
      <div className={clsx('grid place-items-center bg-white/[.03] text-slate-600', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    )
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" className="group relative block">
      <img src={src} alt={alt} className={className} />
      <span className="absolute inset-0 grid place-items-center bg-ink-950/60 opacity-0 transition group-hover:opacity-100">
        <Maximize2 className="h-4 w-4 text-white" />
      </span>
    </a>
  )
}

export default function KycPage() {
  const [tab, setTab] = useState('requests')
  return (
    <>
      <PageHead title="احراز هویت" subtitle="بررسی درخواست‌های ارتقای سطح و کارت‌های بانکی کاربران" />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {[['requests', 'درخواست‌های احراز'], ['cards', 'کارت‌های بانکی'],
          ['identity', 'استعلام هویت']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>{l}</button>
        ))}
      </div>
      {tab === 'requests' ? <Requests /> : tab === 'cards' ? <Cards /> : <IdentityChecks />}
    </>
  )
}

function Requests() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [status, setStatus] = useState('pending')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState<any>(null)
  const [reason, setReason] = useState('')

  const load = () => {
    setLoading(true)
    get(`/admin/kyc/requests${qs({ status, page, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [status, page])

  const act = async (id: number, action: string, why = '') => {
    try {
      await post(`/admin/kyc/requests/${id}/${action}`, { reason: why })
      toast.ok(action === 'approve' ? 'تأیید شد' : 'رد شد')
      setView(null); setReason(''); load()
    } catch (e: any) { toast.err(e.message) }
  }

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }}
            options={[['pending', 'در انتظار بررسی'], ['approved', 'تأییدشده'], ['rejected', 'رد شده'], ['', 'همه']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={880} empty="درخواستی در این وضعیت نیست"
        onRowClick={(r: any) => setView(r)}
        columns={[
          { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
          { key: 'u', header: 'کاربر', cell: (r: any) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
          { key: 'lv', header: 'درخواست', cell: (r: any) => <Tag kind="info">سطح {fa(r.currentLevel)} ← {fa(r.level)}</Tag> },
          {
            key: 'doc', header: 'مدارک', cell: (r: any) => {
              const n = r.documents?.length ?? 0
              const need = r.requiredDocs?.length ?? 0
              if (!need && !n) return <span className="text-slate-600">—</span>
              return <Tag kind={n >= need ? 'up' : 'gold'}>{fa(n)} از {fa(need)}</Tag>
            },
          },
          { key: 'n', header: 'نام', cell: (r: any) => <span className="text-slate-200">{r.fullName || '—'}</span> },
          { key: 'nid', header: 'کد ملی', cell: (r: any) => <span className="num text-slate-300" dir="ltr">{r.nationalId || '—'}</span> },
          { key: 'm', header: 'موبایل', cell: (r: any) => <span className="num text-slate-300" dir="ltr">{r.mobile || '—'}</span> },
          { key: 's', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
          { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
          {
            // درخواستی که مدرک تصویری دارد، از داخل جدول تأیید نمی‌شود:
            // تأییدِ مدرکی که کسی بازش نکرده، همان احراز هویت نمایشی است.
            key: 'x', header: 'بررسی', cell: (r: any) => r.status === 'pending' && (
              <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                {(r.documents?.length ?? 0) === 0 ? (
                  <ConfirmButton variant="up" label="تأیید" question="تأیید شود؟"
                    icon={<Check className="h-3.5 w-3.5" />} onConfirm={() => act(r.id, 'approve')} />
                ) : null}
                <button onClick={() => setView(r)} className="btn-ghost !px-3 !py-1.5 text-xs">
                  <ShieldCheck className="h-3.5 w-3.5" /> بررسی مدارک
                </button>
              </div>
            ),
          },
        ]} />
      <Pager meta={meta} onPage={setPage} />

      {/*
        کشو عمداً «پهن» است. کار این صفحه نگاه‌کردن به تصویر کارت ملی و
        سلفی است؛ در عرضِ باریکِ پیش‌فرض، هر مدرک حدود ۱۲۰ پیکسل پهنا
        می‌گرفت و عملاً خوانا نبود — یعنی کارشناس یا باید مدرکِ نادیده را
        تأیید می‌کرد یا بی‌دلیل رد.
      */}
      <Drawer wide open={!!view} onClose={() => setView(null)} title="جزئیات درخواست احراز هویت">
        {view && (
          <>
            <div className="mb-4 grid gap-2 sm:grid-cols-2">
              {[['ایمیل', view.email], ['نام و نام خانوادگی', view.fullName], ['کد ملی', view.nationalId],
                ['موبایل', view.mobile], ['تاریخ تولد', view.birthDate], ['نشانی', view.address],
                ['سطح درخواستی', `سطح ${fa(view.level)}`]].map(([k, v]) => (
                <div key={k as string} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                  <div className="mb-1 text-[10px] text-slate-500">{k}</div>
                  <div className="num text-sm text-slate-200">{v || '—'}</div>
                </div>
              ))}
            </div>
            {/* کارت‌های بانکی همان کاربر — شرط اصلی سطح دو */}
            {(view.cards?.length ?? 0) > 0 && (
              <div className="mb-4">
                <div className="mb-2 text-xs font-bold text-slate-300">کارت‌های بانکی کاربر</div>
                <div className="space-y-1.5">
                  {view.cards.map((c: any, i: number) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border border-white/[.06] bg-white/[.02] p-2.5 text-xs">
                      <CreditCard className="h-3.5 w-3.5 text-slate-500" />
                      <span className="num text-slate-200" dir="ltr">{c.masked}</span>
                      <span className="text-slate-400">{c.bank || '—'}</span>
                      <span className="num text-slate-500" dir="ltr">{c.iban || ''}</span>
                      <span className="mr-auto"><Status value={c.status} /></span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* مدارک ارسالی */}
            <div className="mb-4">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-bold text-slate-300">
                مدارک ارسالی
                {(view.requiredDocs?.length ?? 0) > 0 && (
                  <span className="font-normal text-slate-500">
                    (لازم برای این سطح: {view.requiredDocs.join('، ')})
                  </span>
                )}
              </div>
              {(view.documents?.length ?? 0) === 0 ? (
                <div className="flex items-center gap-2 rounded-xl border border-white/[.06] bg-white/[.02] p-3 text-[11px] text-slate-500">
                  <ImageOff className="h-4 w-4" /> مدرکی پیوست نشده است
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {view.documents.map((d: any) => (
                    <div key={d.index} className="overflow-hidden rounded-xl border border-white/[.06] bg-white/[.02]">
                      {/*
                        `object-contain` و نه `object-cover`: برشِ تصویر برای
                        خوش‌قوارگی، دقیقاً همان گوشه‌ای از کارت ملی را می‌بُرد
                        که شماره و تاریخ انقضا در آن است. اینجا خوانا بودن
                        مهم‌تر از قاب‌بندی است.
                      */}
                      <AuthImage url={d.url} alt={d.label} className="h-52 w-full bg-ink-950 object-contain" />
                      <div className="p-2 text-[10px] text-slate-400">{d.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/*
              ── چرا این بخش چسبیده به پایین است ──────────────────────────
              محتوای این کشو بلند است: هفت ردیف اطلاعات، کارت‌های بانکی و
              تصویر مدارک. دکمه‌های تأیید و رد ته این فهرست بودند و کشو هم
              اسکرول‌بارِ کم‌رنگی داشت، پس کارشناس صفحه را «ناقص» می‌دید و
              فکر می‌کرد راهی برای تأیید یا رد نیست.

              با `sticky` این بخش همیشه در دید می‌ماند، هرجای فهرست که
              باشید. `-mx-5 px-5` هم پدینگ کشو را جبران می‌کند تا نوار تا
              لبه‌ها کشیده شود و مثل یک پایه‌ی واقعی دیده شود.

              عددِ `-bottom-5` عمدی است و نه `bottom-0`: محدوده‌ی چسبندگی،
              کادرِ پدینگِ ظرفِ اسکرول است، پس با `bottom-0` نوار دقیقاً به
              اندازه‌ی پدینگِ پایینِ کشو (۲۰ پیکسل) بالاتر می‌ایستد و یک
              نوارِ باریک می‌ماند که محتوا از زیرش رد می‌شود — روی گوشی
              کاملاً شبیه یک ایرادِ رندر دیده می‌شود. اندازه‌گیری شد:
              `bottom-0` فاصله‌ی ۲۰ پیکسل، `-bottom-5` فاصله‌ی صفر.
            */}
            {view.status === 'pending' && (
              <div className="sticky -bottom-5 -mx-5 -mb-5 border-t border-white/10 bg-ink-900/98 px-5 pb-5 pt-4 backdrop-blur-xl">
                <label className="label">دلیل رد (در صورت رد کردن)</label>
                <textarea className="input mb-3 min-h-[70px]" value={reason}
                  onChange={(e) => setReason(e.target.value)} placeholder="مثلاً تصویر کارت ملی خوانا نیست" />
                <div className="flex gap-2">
                  <button onClick={() => act(view.id, 'approve')} className="btn-up flex-1 !py-3">
                    <Check className="h-4 w-4" /> تأیید سطح {fa(view.level)}
                  </button>
                  <button onClick={() => act(view.id, 'reject', reason)} className="btn-down flex-1 !py-3">
                    <X className="h-4 w-4" /> رد درخواست
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Drawer>
    </Panel>
  )
}

function Cards() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/kyc/cards${qs({ status, page, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [status, page])

  const act = async (id: number, action: string) => {
    try { await post(`/admin/kyc/cards/${id}/${action}`); toast.ok('انجام شد'); load() }
    catch (e: any) { toast.err(e.message) }
  }

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }}
            options={[['', 'همه'], ['pending', 'در انتظار'], ['approved', 'تأییدشده'], ['rejected', 'رد شده']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={760} empty="کارتی ثبت نشده است" columns={[
        { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r: any) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 'c', header: 'شماره کارت', cell: (r: any) => <span className="num tracking-wider text-white" dir="ltr">{r.cardNumber}</span> },
        { key: 'ib', header: 'شبا', cell: (r: any) => <span className="num text-slate-400" dir="ltr">{r.iban || '—'}</span> },
        { key: 'b', header: 'بانک', cell: (r: any) => <span className="text-slate-300">{r.bankName}</span> },
        { key: 's', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
        { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt, false)}</span> },
        {
          key: 'x', header: 'بررسی', cell: (r: any) => (
            <div className="flex gap-1.5">
              {r.status !== 'approved' && (
                <ConfirmButton variant="up" label="تأیید" question="تأیید؟"
                  icon={<Check className="h-3.5 w-3.5" />} onConfirm={() => act(r.id, 'approve')} />
              )}
              {r.status !== 'rejected' && (
                <ConfirmButton variant="down" label="رد" question="رد شود؟"
                  icon={<X className="h-3.5 w-3.5" />} onConfirm={() => act(r.id, 'reject')} />
              )}
            </div>
          ),
        },
      ]} />
      <Pager meta={meta} onPage={setPage} />
    </Panel>
  )
}


/* ------------------------------------------------- استعلام هویت (جیبیت) */
function IdentityChecks() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [status, setStatus] = useState<any>(null)
  const [f, setF] = useState({ q: '', status: '', page: 1 })
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/kyc/identity-checks${qs({ ...f, per_page: 30 })}`)
      .then((r: any) => { setRows(asArray(r.items)); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(() => {
    get('/admin/kyc/identity-status').then(setStatus).catch(() => {})
    const id = setTimeout(load, f.q ? 350 : 0)
    return () => clearTimeout(id)
  }, [f])

  // اعتبار جیبیت پیش‌پرداخت است؛ با تمام‌شدنش ثبت‌نام متوقف می‌شود
  const lowBalance = status && status.configured && status.balanceIrt < 200_000

  return (
    <>
      {status && (
        <div className={clsx('mb-4 rounded-2xl border p-4',
          lowBalance ? 'border-down/30 bg-down/[.07]' : 'border-white/[.07] bg-white/[.03]')}>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-200">
            <Fingerprint className="h-4 w-4" /> سرویس استعلام هویت
            <span className={clsx('chip !py-0.5 text-[10px]',
              status.enabled ? 'border-up/30 text-up' : 'border-slate-500/30 text-slate-400')}>
              {status.enabled ? 'فعال' : 'خاموش'}
            </span>
            {status.onRegister && (
              <span className="chip !py-0.5 text-[10px] border-brand-500/30 text-brand-200">
                الزامی هنگام ثبت‌نام
              </span>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="اعتبار باقیمانده" tone={lowBalance ? 'warn' : undefined}
              value={status.balanceError ? '—' : `${num(status.balanceIrt)} تومان`} />
            <Stat label="تأییدشده (۳۰ روز)" value={fa(status.stats?.matched ?? 0)} />
            <Stat label="رد‌شده" value={fa(status.stats?.mismatch ?? 0)} />
            <Stat label="خطای سرویس" value={fa(status.stats?.error ?? 0)}
              tone={(status.stats?.error ?? 0) > 0 ? 'warn' : undefined} />
          </div>

          {lowBalance && (
            <div className="mt-3 flex items-start gap-2 text-[11px] leading-6 text-down">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              اعتبار جیبیت رو به اتمام است. با تمام‌شدن آن، <b>ثبت‌نام کاربران متوقف
              می‌شود</b> — حساب جیبیت را شارژ کنید.
            </div>
          )}
          {status.balanceError && (
            <div className="mt-3 text-[11px] text-slate-500">
              <Wallet className="ml-1 inline h-3.5 w-3.5" />
              اعتبار خوانده نشد: {status.balanceError}
            </div>
          )}
        </div>
      )}

      <Panel dense>
        <div className="p-4 pb-0">
          <Toolbar>
            <input className="input !py-2 text-xs" placeholder="کد ملی، موبایل یا نام خانوادگی…"
              value={f.q} onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} />
            <Select value={f.status} onChange={(v) => setF({ ...f, status: v, page: 1 })}
              options={[['', 'همه'], ['matched', 'تأیید'], ['mismatch', 'رد'],
                        ['error', 'خطای سرویس']]} />
            <RefreshButton onClick={load} loading={loading} />
          </Toolbar>
        </div>
        <DataTable rows={rows} loading={loading} minWidth={980} columns={[
          { key: 'd', header: 'زمان', cell: (r) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
          { key: 'n', header: 'نام', cell: (r) => <span className="text-slate-200">{r.name || '—'}</span> },
          { key: 'nid', header: 'کد ملی', cell: (r) => <span className="num text-slate-300" dir="ltr">{r.nationalId}</span> },
          { key: 'm', header: 'موبایل', cell: (r) => <span className="num text-slate-300" dir="ltr">{r.mobile}</span> },
          { key: 'nm', header: 'نام ↔ کد ملی', cell: (r) => <Mark v={r.nameMatch} /> },
          { key: 'mm', header: 'موبایل ↔ کد ملی', cell: (r) => <Mark v={r.mobileMatch} /> },
          { key: 's', header: 'نتیجه', cell: (r) => (
            <span className={clsx('text-xs', r.status === 'matched' ? 'text-up'
              : r.status === 'error' ? 'text-gold' : 'text-down')}>
              {{ matched: 'تأیید', mismatch: 'رد', error: 'خطای سرویس' }[r.status] ?? r.status}
            </span>) },
          { key: 'msg', header: 'پیام', cell: (r) => <span className="block max-w-[16rem] truncate text-slate-400">{r.message || '—'}</span> },
        ]} />
        <Pager meta={meta} onPage={(page) => setF({ ...f, page })} />
      </Panel>
    </>
  )
}

function Mark({ v }: { v: boolean | null }) {
  if (v === null || v === undefined) return <span className="text-slate-600">—</span>
  return v ? <Check className="h-4 w-4 text-up" /> : <X className="h-4 w-4 text-down" />
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className={clsx('num mt-1 text-sm font-semibold',
        tone === 'warn' ? 'text-down' : 'text-white')}>{value}</div>
    </div>
  )
}
